import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { Client } from '../models/client.model';

// import { AngularFireAuth } from '@angular/fire/compat/auth';

const BASE_URL = environment.base_url;

export interface ClientPermissions {
  cleanScan: boolean,
  designScan: boolean
}

@Injectable({
  providedIn: 'root'
})
export class ClientsService {

  constructor(
    private http: HttpClient
  ) { }

  public clientUpdated: EventEmitter<string> = new EventEmitter<string>();


  getClients(): Observable<Client[]> {

    const url = `${BASE_URL}/clients`;

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get(url, { headers }).pipe(
      map((resp: any) => resp.clients)
    )

  }

  getClientsByDistributorId(distributorId: string): Observable<Client[]> {

    const url = `${BASE_URL}/clients?distributorId=${distributorId}`;

    return this.http.get(url).pipe(
      map((resp: any) => resp.clients)
    )

  }

  getClient(clientEmail: string): Observable<Client> {
    const url = `${BASE_URL}/client/${clientEmail}`;

    return this.http.get(url).pipe(
      map((resp: any) => resp.client)
    )
  }

  getClientById(clientId: string): Observable<Client> {
    const url = `${BASE_URL}/client/by/${clientId}`;

    return this.http.get(url).pipe(
      map((resp: any) => resp.client)
    )
  }

  create(formData: any) {

    // const fireUser = from(this.fireAuth.createUserWithEmailAndPassword(formData.email, formData.password))

    const url = `${BASE_URL}/client`;

    return this.http.post(url, formData).pipe(
      map((resp) => resp)
    )
  }

  updateClientFreeTrial(userLogin: string) {
    const url = `${BASE_URL}/client?partToUpdate=freeTrial`

    const body = {
      ID: userLogin
    }

    return this.http.put(url, body)
  }

  deleteClientDetail(clientId: any) {
    const url = `${BASE_URL}/client/${clientId}`
    return this.http.delete(url)
  }

  createUserAccess(formData: any) {

    const url = `${BASE_URL}/clientAccess`;
    return this.http.post(url, formData).pipe(
      map((resp) => resp)
    )
  }


  // createUserOnFireBase(formData: any) {

  //   const fireUser = from(this.fireAuth.createUserWithEmailAndPassword(formData.email, formData.password))
  //   return fireUser;
  // }


  // sendRestorePassword(email: string) {
  //   const restore = from(this.fireAuth.sendPasswordResetEmail(email))

  //   return restore;
  // }


  billToClient(client: Client) {

    const url = `${BASE_URL}/client/billToggle`;

    return this.http.post(url, {
      clientID: client.ID,
      service: "scans"
    }).pipe(
      map((resp) => resp)
    )
  }

  autoAlignScans(client: Client) {

    const url = `${BASE_URL}/client/autoAlignScans`


    return this.http.post(url, {
      clientID: client.ID
    }).pipe(
      map((resp) => resp)
    )
  }

  billAutoalign(client: Client) {

    const url = `${BASE_URL}/client/billToggle`

    return this.http.post(url, {
      clientID: client.ID,
      service: 'auto align'
    }).pipe(
      map((resp) => resp)
    )
  }

  billInsoleDesign(client: Client) {

    const url = `${BASE_URL}/client/billToggle`
    return this.http.post(url, {
      clientID: client.ID,
      service: '3d insoles design'
    }).pipe(
      map((resp) => resp)
    )
  }

  generatePDFReports(client: Client) {

    const url = `${BASE_URL}/client/pressurePlateReportGeneration`

    return this.http.post(url, {
      clientID: client.ID
    }).pipe(
      map((resp) => resp)
    )
  }

  billPDFReports(client: Client) {

    const url = `${BASE_URL}/client/billToggle`

    return this.http.post(url, {
      clientID: client.ID,
      service: 'pressure plate'
    }).pipe(
      map((resp) => resp)
    )
  }

  updateIdentifier(client: Client) {
    const url = `${BASE_URL}/client?partToUpdate=clientIdentifier`
    return this.http.put(url, {
      clientID: client.ID,
      identifier: client.identifier
    })
  }

  updateIdentifiersList(client: Client) {
    const url = `${BASE_URL}/client?partToUpdate=clientIdentifierList`
    return this.http.put(url, {
      clientID: client.ID,
      identifiersList: client.identifiersList
    })
  }

  updateEngraving(clientID: string, engraving: {
    text: string,
    engravedLogo: boolean,
    logo?: string | null
  }) {

    const engravingPayload = {
      text: engraving.text,
      engravedLogo: engraving.engravedLogo,
      ...(engraving.logo && { logo: engraving.logo })
    };

    return this.http.put(`${BASE_URL}/client/engraving`, { clientID, engraving: engravingPayload });
  }

  updateiOSPreferences(clientID: string, scanTypesAllowed: {
    plantarSurface: boolean,
    dualFoam: boolean,
    foam: boolean,
    torso: boolean,
    foot: boolean,
    leg: boolean
  }) {

    return this.http.put(`${BASE_URL}/client/iOSPreferences`, { clientID, iosPreferences: { threeDScan: scanTypesAllowed } });
  }

  updateClientDetails(clientID: string, clientData: any) {
    const { name, email, description } = clientData

    const body = {
      clientID,
      name,
      email,
      description
    }

    return this.http.put(`${BASE_URL}/client?partToUpdate=clientDetails`, body);
  }

  updateScanProcessingProperties(clientID: string, scanProcessingProperties: any) {
    const url = `${BASE_URL}/client/scanProcessingProperties`
    const token = localStorage.getItem('token') || '';

    const headers = {
      'X-Amz-Security-Token': token
    }

    return this.http.put(url, {
      clientID,
      scanProcessingProperties
    }, { headers })
  }

  billSpinalBraceDesign(client: Client) {

    const url = `${BASE_URL}/client/billToggle`
    const token = localStorage.getItem('token') || '';

    const headers = {
      'X-Amz-Security-Token': token
    }

    return this.http.post(url, {
      clientID: client.ID,
      service: '3D design spinal brace'
    }, { headers }).pipe(
      map((resp) => resp)
    )
  }

  billAFODesign(client: Client) {

    const url = `${BASE_URL}/client/billToggle`
    const token = localStorage.getItem('token') || '';

    const headers = {
      'X-Amz-Security-Token': token
    }

    return this.http.post(url, {
      clientID: client.ID,
      service: '3D design afo'
    }, { headers }).pipe(
      map((resp) => resp)
    )
  }

  updateClientUsers(clientID, users) {

    const body = {
      clientID,
      users
    }

    return this.http.put(`${BASE_URL}/client?partToUpdate=clientUsers`, body);
  }

  updateClientPermissions(clientID: string, permissions: ClientPermissions) {

    const body = {
      clientID,
      permissions
    }

    return this.http.put(`${BASE_URL}/client?partToUpdate=clientPermissions`, body);
  }
}
